import type { ReactNode } from "react";
import React, { useMemo } from "react";
import { BreadcrumbList, WithContext } from "schema-dts";

import { useLexicon } from "../context/LexiconContext";
import useBreadcrumbData from "../hooks/useBreadcrumbData";
import { BreadcrumbItem, BreadcrumbItemProps } from "./BreadcrumbItem";
import JSONLDTag from "./jsonld/JSONLDTag";

type LinkedData = WithContext<BreadcrumbList>;

type Props = {
  children: ReactNode;
};

const isBreadcrumbItem = (
  child: React.ReactChild | React.ReactFragment | React.ReactPortal
): child is React.ReactElement<BreadcrumbItemProps> =>
  React.isValidElement(child) && child.type === BreadcrumbItem;

export function Breadcrumb({ children }: Props) {
  const lexicon = useLexicon();
  const { website } = useBreadcrumbData();
  const linkedData = useMemo<LinkedData>(
    () => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: lexicon.home,
          item: website.absolutePath,
        },
        ...React.Children.toArray(children)
          .filter(isBreadcrumbItem)
          .map((child, index) => ({
            "@type": "ListItem" as const,
            position: index + 2,
            name: child.props.label,
            item: child.props.to,
          })),
      ],
    }),
    [children, lexicon.home, website.absolutePath]
  );

  return (
    <nav aria-label="Breadcrumb">
      <JSONLDTag<BreadcrumbList>
        data-testid="breadcrumb-ld"
        json={linkedData}
      />
      <ol aria-label="breadcrumb" className="flex flex-wrap p-5 list-none">
        <BreadcrumbItem to={website.absolutePath} label={lexicon.home} />
        {children}
      </ol>
    </nav>
  );
}
